import React from "react";
import { cn } from "@/lib/utils";
import { Download, Upload, Eraser, ChevronDown, ChevronUp } from "lucide-react";

interface FormSection {
    id: string;
    icon: React.ReactNode;
    label: string;
    content: React.ReactNode;
    hasTemplate?: boolean;
    hasClear?: boolean;
    required?: boolean;
}

interface FormSectionWrapperProps {
    section: FormSection;
    isMinimized: boolean;
    hasErrors: boolean;
    sectionCount?: number | null;
    onMinimize: (sectionId: string) => void;
    onExpand: (sectionId: string) => void;
    onLoadTemplate?: (type: string) => void;
    onSaveTemplate?: (type: string) => void;
    onClearSection?: (sectionId: string, label: string) => void;
}

const getTemplateType = (sectionId: string): string => {
    switch (sectionId) {
        case "complaints":     return "complaint";
        case "medicines":      return "medicine";
        case "investigations": return "investigation";
        case "advice":         return "advice";
        case "diagnosis":      return "diagnosis";
        case "notes":          return "quick_notes";
        default:               return "quick_notes";
    }
};

// ─── Minimized ────────────────────────────────────────────────────────────────

const MinimizedSection = ({
    section, hasErrors, sectionCount, onExpand,
}: {
    section: FormSection;
    hasErrors: boolean;
    sectionCount?: number | null;
    onExpand: (id: string) => void;
}) => (
    <div
        className={cn(
            "flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none",
            hasErrors && "bg-destructive/5"
        )}
        onClick={() => onExpand(section.id)}
    >
        <div className="flex items-center gap-2">
            <span className={cn("text-muted-foreground", hasErrors && "text-destructive")}>
                {section.icon}
            </span>
            <span className={cn("text-xs font-semibold", hasErrors ? "text-destructive" : "text-foreground")}>
                {section.label}
            </span>
            {sectionCount != null && sectionCount > 0 && (
                <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full tabular-nums">
                    {sectionCount}
                </span>
            )}
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
    </div>
);

// ─── Expanded ─────────────────────────────────────────────────────────────────

const ExpandedSection = ({
    section, hasErrors, sectionCount,
    onMinimize, onLoadTemplate, onSaveTemplate, onClearSection,
}: {
    section: FormSection;
    hasErrors: boolean;
    sectionCount?: number | null;
    onMinimize: (id: string) => void;
    onLoadTemplate?: (type: string) => void;
    onSaveTemplate?: (type: string) => void;
    onClearSection?: (sectionId: string, label: string) => void;
}) => (
    <div className={cn("py-1", hasErrors && "bg-destructive/5")}>
        <div className="flex">

            {/* Left sidebar — label + actions */}
            <div className="w-28 flex-shrink-0 p-2 flex flex-col">

                {/* Label row with collapse button */}
                <div className="flex items-start justify-between mb-2">
                    <h3 className={cn(
                        "text-xs font-semibold leading-tight",
                        hasErrors ? "text-destructive" : "text-muted-foreground"
                    )}>
                        {section.label}
                        {section.required && <span className="text-destructive ml-0.5">*</span>}
                    </h3>
                    <button
                        type="button"
                        onClick={() => onMinimize(section.id)}
                        className="text-muted-foreground/40 hover:text-muted-foreground transition-colors ml-1 flex-shrink-0"
                        title="Collapse section"
                    >
                        <ChevronUp className="w-3 h-3" />
                    </button>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1">
                    {section.hasTemplate && onLoadTemplate && (
                        <button
                            type="button"
                            onClick={() => onLoadTemplate(getTemplateType(section.id))}
                            className="flex items-center justify-center w-6 h-6 text-muted-foreground/50 hover:text-primary hover:bg-primary/10 rounded transition-all duration-150"
                            title="Load Template"
                        >
                            <Download className="w-3 h-3" />
                        </button>
                    )}
                    {section.hasTemplate && onSaveTemplate && (
                        <button
                            type="button"
                            onClick={() => onSaveTemplate(getTemplateType(section.id))}
                            className="flex items-center justify-center w-6 h-6 text-muted-foreground/50 hover:text-success hover:bg-success/10 rounded transition-all duration-150"
                            title="Save as Template"
                        >
                            <Upload className="w-3 h-3" />
                        </button>
                    )}
                    {section.hasClear && onClearSection && (
                        <button
                            type="button"
                            onClick={() => onClearSection(section.id, section.label)}
                            className="flex items-center justify-center w-6 h-6 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 rounded transition-all duration-150"
                            title="Clear Section"
                        >
                            <Eraser className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-2 min-w-0">
                {section.content}
            </div>
        </div>
    </div>
);

// ─── Export ───────────────────────────────────────────────────────────────────

export default function FormSectionWrapper({
    section, isMinimized, hasErrors, sectionCount,
    onMinimize, onExpand, onLoadTemplate, onSaveTemplate, onClearSection,
}: FormSectionWrapperProps) {
    if (isMinimized) {
        return (
            <MinimizedSection
                section={section}
                hasErrors={hasErrors}
                sectionCount={sectionCount}
                onExpand={onExpand}
            />
        );
    }

    return (
        <ExpandedSection
            section={section}
            hasErrors={hasErrors}
            sectionCount={sectionCount}
            onMinimize={onMinimize}
            onLoadTemplate={onLoadTemplate}
            onSaveTemplate={onSaveTemplate}
            onClearSection={onClearSection}
        />
    );
}
